/**
 * Fit Finder — assets/fit-finder.js
 * Optimised for Symmetry theme — Athletik Tween
 *
 * RECOMMENDATION ENGINE:
 * Uses the brand's actual size chart measurements.
 * Scores each available size against the child's height + weight,
 * then applies build and fit preference adjustments.
 * Works for any product regardless of which sizes it stocks.
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────
     SELECTORS — Symmetry theme
  ───────────────────────────────────────────── */
  var SELECTORS = {
    sizeRadio: [
      '.option-selector input[type="radio"]',
      'fieldset.option-selector input[type="radio"]',
      'input[type="radio"][name*="Size"]',
      'input[type="radio"][name*="size"]',
    ].join(','),
    sizeSelect: [
      'select.option-selector__select',
      'select[name*="Size"]',
      'select[name*="size"]',
    ].join(','),
  };

  /* ─────────────────────────────────────────────
     SIZES — 100% from product variants
     window.fitFinderSizes injected by fit-finder.liquid
  ───────────────────────────────────────────── */
  var SIZES = (window.fitFinderSizes && window.fitFinderSizes.length)
    ? window.fitFinderSizes
    : [];

  if (!SIZES.length) {
    console.warn('[FitFinder] No sizes found. Check fit-finder.liquid.');
  }

  /* ─────────────────────────────────────────────
     BRAND SIZE CHART — Athletik Tween actual data
     Source: athletiktween.com.au size guide
     height = typical height (cm) for this size
     chest/waist/hip = body measurements (cm)
     heightRange = [min, max] height this size fits
  ───────────────────────────────────────────── */
  var SIZE_CHART = {
    '8Y':     { height: 131, heightRange: [120, 136], chest: 68, waist: 58, hip: 72 },
    '9Y':     { height: 141, heightRange: [136, 145], chest: 70, waist: 60, hip: 74 },
    '10Y':    { height: 148, heightRange: [145, 152], chest: 72, waist: 62, hip: 76 },
    '11-12Y': { height: 156, heightRange: [152, 160], chest: 76, waist: 64, hip: 81 },
    '13-14Y': { height: 164, heightRange: [160, 175], chest: 80, waist: 68, hip: 86 },
  };

  /* ─────────────────────────────────────────────
     RECOMMENDATION ENGINE
     
     Strategy:
     1. Score each available size by how well height matches
     2. Weight gives a secondary signal for build
     3. Stated build adjusts the index up/down
     4. Fit preference adjusts the final index
     
     Height is weighted 70% — most reliable signal.
     Weight is weighted 30% — secondary signal.
  ───────────────────────────────────────────── */
  function scoreSize(sizeKey, heightCm, weightKg) {
    var chart = SIZE_CHART[sizeKey];

    if (!chart) {
      // Size not in chart — use position in SIZES array as fallback
      var idx = SIZES.indexOf(sizeKey);
      var mid = (SIZES.length - 1) / 2;
      return 50 - Math.abs(idx - mid) * 10;
    }

    var score = 0;

    // HEIGHT SCORE (70% weight)
    // Perfect score if height is within the size's range
    // Penalise by how far outside the range
    var hMin = chart.heightRange[0];
    var hMax = chart.heightRange[1];
    if (heightCm >= hMin && heightCm <= hMax) {
      score += 70; // perfect height match
    } else {
      var hDiff = heightCm < hMin
        ? hMin - heightCm
        : heightCm - hMax;
      score += Math.max(0, 70 - hDiff * 3);
    }

    // WEIGHT SCORE (30% weight) — only if provided
    if (weightKg && weightKg > 0) {
      // Estimate expected weight from chest measurement (rough proxy)
      // Average weight for children scales roughly with height
      var expectedWeight = (chart.height - 100) * 0.9; // rough formula
      var wDiff = Math.abs(weightKg - expectedWeight);
      score += Math.max(0, 30 - wDiff * 1.5);
    } else {
      // No weight provided — give neutral score
      score += 15;
    }

    return score;
  }

  function recommendSize(height, weight, build, fit) {
    if (!SIZES.length) return null;

    // Score every available size
    var scores = SIZES.map(function (size) {
      return { size: size, score: scoreSize(size, height, weight) };
    });

    // Sort by score descending
    scores.sort(function (a, b) { return b.score - a.score; });

    // Best match index in SIZES array
    var bestSize  = scores[0].size;
    var bestIdx   = SIZES.indexOf(bestSize);
    var secondIdx = SIZES.indexOf(scores[1] ? scores[1].size : bestSize);

    // BUILD ADJUSTMENT
    // Slim: if borderline (scores close), prefer smaller size
    // Broad: if borderline or within range, prefer larger size
    var scoreDiff = scores[0].score - (scores[1] ? scores[1].score : 0);
    var borderline = scoreDiff < 15; // scores within 15 points = borderline

    if (build === 'slim' && borderline && secondIdx < bestIdx) {
      bestIdx = secondIdx;
    }
    if (build === 'broad') {
      bestIdx = Math.min(SIZES.length - 1, bestIdx + 1);
    }

    // FIT PREFERENCE ADJUSTMENT
    if (fit === 'room') {
      bestIdx = Math.min(SIZES.length - 1, bestIdx + 1);
    }
    if (fit === 'relaxed') {
      bestIdx = Math.min(SIZES.length - 1, bestIdx + 1);
    }
    // 'perfect' = no adjustment, use best match as-is

    return SIZES[bestIdx];
  }

  function getNextSize(size) {
    var i = SIZES.indexOf(size);
    return i >= 0 && i < SIZES.length - 1 ? SIZES[i + 1] : null;
  }

  function getConfidence(heightCm, size, build, fit) {
    var chart = SIZE_CHART[size];
    if (!chart) return 90;

    var hMin = chart.heightRange[0];
    var hMax = chart.heightRange[1];

    if (heightCm >= hMin && heightCm <= hMax) {
      // Height perfectly within range
      if (build === 'regular' && fit === 'perfect') return 99;
      if (fit === 'perfect') return 97;
      return 95;
    }

    // Height just outside range
    var diff = heightCm < hMin ? hMin - heightCm : heightCm - hMax;
    if (diff <= 3) return 93;
    if (diff <= 6) return 90;
    return 87;
  }

  /* ─────────────────────────────────────────────
     SMART SIZE LABEL PARSER
     No emojis — works for any number of sizes
  ───────────────────────────────────────────── */
  function parseSizeLabel(size) {
    var s = String(size).trim();
    var rangeMatch = s.match(/^(\d+)\s*[-–]\s*(\d+)/);
    if (rangeMatch) return 'Ages ' + rangeMatch[1] + '–' + rangeMatch[2];
    var singleMatch = s.match(/^(\d+)/);
    if (singleMatch) return 'Age ' + singleMatch[1];
    return s;
  }

  /* ─────────────────────────────────────────────
     VARIANT SELECTION — Symmetry theme
  ───────────────────────────────────────────── */
  function applySize(sizeKey) {
    var applied = false;

    var radios = document.querySelectorAll(SELECTORS.sizeRadio);
    radios.forEach(function (radio) {
      var val = (radio.value || '').trim();
      if (val.toLowerCase() === sizeKey.toLowerCase()) {
        var groupName = radio.name;
        if (groupName) {
          document.querySelectorAll('input[type="radio"][name="' + groupName + '"]').forEach(function (r) {
            r.checked = false;
          });
        }
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        radio.dispatchEvent(new Event('input',  { bubbles: true }));
        var label = document.querySelector('label[for="' + radio.id + '"]');
        if (label) label.click();
        applied = true;
      }
    });

    if (!applied) {
      var selects = document.querySelectorAll(SELECTORS.sizeSelect);
      selects.forEach(function (sel) {
        Array.from(sel.options).forEach(function (opt) {
          if (opt.value.toLowerCase() === sizeKey.toLowerCase() ||
              opt.text.trim().toLowerCase() === sizeKey.toLowerCase()) {
            sel.value = opt.value;
            sel.dispatchEvent(new Event('change', { bubbles: true }));
            applied = true;
          }
        });
      });
    }

    document.dispatchEvent(new CustomEvent('fitfinder:sizeSelected', {
      bubbles: true,
      detail: { size: sizeKey, applied: applied },
    }));

    if (!applied) {
      console.warn('[FitFinder] Could not select size "' + sizeKey + '". Check SELECTORS in fit-finder.js.');
    }

    return applied;
  }

  /* ─────────────────────────────────────────────
     QUIZ STATE
  ───────────────────────────────────────────── */
  var state = {
    step: 0,
    age: null,
    height: 141,
    weight: 32,
    build: null,
    fit: null,
  };

  /* ─────────────────────────────────────────────
     HTML BUILDERS
  ───────────────────────────────────────────── */
  function dots(current) {
    var html = '<div class="ff-dots">';
    for (var i = 0; i < 5; i++) {
      html += '<div class="ff-dot' + (i <= current ? ' active' : '') +
              '" style="width:' + (i <= current ? '20' : '8') + 'px"></div>';
    }
    return html + '</div>';
  }

  function renderIntro() {
    return (
      '<div class="ff-intro-hero">' +
        '<div style="font-size:34px">✨</div>' +
        '<h2>Find Her Perfect Fit</h2>' +
        '<p>Answer 4 quick questions about your daughter. No tape measure, no guessing — just the right size, first time.</p>' +
      '</div>' +
      '<ul class="ff-feature-list">' +
        '<li><div class="ff-feature-icon">📏</div><span><strong>No tape measure needed</strong> — we only ask what you already know</span></li>' +
        '<li><div class="ff-feature-icon">⚡</div><span><strong>Takes 30 seconds</strong> — four simple questions</span></li>' +
        '<li><div class="ff-feature-icon">🎯</div><span><strong>Based on your actual size chart</strong> — not a generic guess</span></li>' +
        '<li><div class="ff-feature-icon">🌱</div><span><strong>Growth-aware</strong> — we\'ll suggest when to size up</span></li>' +
      '</ul>' +
      '<button class="ff-btn-primary" data-action="go" data-step="1">Let\'s Find Her Size →</button>'
    );
  }

  function renderAge() {
    var grid = '<div class="ff-size-grid">';
    SIZES.forEach(function (size) {
      var label = parseSizeLabel(size);
      var chart = SIZE_CHART[size];
      var heightHint = chart ? size + ' · ' + chart.height + 'cm' : size;
      grid +=
        '<div class="ff-size-card' + (state.age === size ? ' selected' : '') +
        '" data-action="pick" data-key="age" data-val="' + size + '">' +
          '<div class="ff-size-card__badge">' + size + '</div>' +
          '<div class="ff-size-card__label">' + label + '</div>' +
          '<div class="ff-size-card__hint">' + (chart ? chart.height + ' cm' : '') + '</div>' +
        '</div>';
    });
    grid += '</div>';

    return dots(0) +
      '<h2 class="ff-title">What size is she currently wearing? 👕</h2>' +
      '<p class="ff-sub">Pick the closest size — we\'ll fine-tune from there</p>' +
      grid +
      '<div class="ff-btn-row">' +
        '<button class="ff-btn-secondary" data-action="go" data-step="0">← Back</button>' +
        '<button class="ff-btn-primary" data-action="go" data-step="2"' + (!state.age ? ' disabled' : '') + '>Next →</button>' +
      '</div>';
  }

  function renderHeight() {
    return dots(1) +
      '<h2 class="ff-title">How tall is she? 📐</h2>' +
      '<p class="ff-sub">Approximate is perfectly fine — just your best guess!</p>' +
      '<div class="ff-slider-row">' +
        '<div class="ff-slider-header">' +
          '<span class="ff-slider-label">Height</span>' +
          '<span class="ff-slider-val" id="ff-height-val">' + state.height + ' cm</span>' +
        '</div>' +
        '<input type="range" class="ff-range" id="ff-height" min="115" max="175" step="1" value="' + state.height + '">' +
        '<div class="ff-slider-ticks"><span>115 cm</span><span>175 cm</span></div>' +
      '</div>' +
      '<div class="ff-slider-row">' +
        '<div class="ff-slider-header">' +
          '<span class="ff-slider-label">Weight <small>(optional)</small></span>' +
          '<span class="ff-slider-val" id="ff-weight-val">' + state.weight + ' kg</span>' +
        '</div>' +
        '<input type="range" class="ff-range" id="ff-weight" min="20" max="75" step="1" value="' + state.weight + '">' +
        '<div class="ff-slider-ticks"><span>20 kg</span><span>75 kg</span></div>' +
      '</div>' +
      '<div class="ff-tip">💡 <strong>Tip:</strong> If you measured her recently, add 1–2 cm for a little growing room.</div>' +
      '<div class="ff-btn-row">' +
        '<button class="ff-btn-secondary" data-action="go" data-step="1">← Back</button>' +
        '<button class="ff-btn-primary" data-action="go" data-step="3">Next →</button>' +
      '</div>';
  }

  function renderBuild() {
    var opts = [
      { v: 'slim',    l: 'Slim',            d: 'Lean and narrow — clothes often feel a little loose', e: '🧍' },
      { v: 'regular', l: 'Regular',          d: 'Average proportions — most standard sizes fit well', e: '🧑' },
      { v: 'broad',   l: 'Broad / Athletic', d: 'Stronger or wider — sometimes needs to size up', e: '🏋️' },
    ];
    var cards = opts.map(function (o) {
      return '<div class="ff-option-card' + (state.build === o.v ? ' selected' : '') +
             '" data-action="pick" data-key="build" data-val="' + o.v + '">' +
        '<span class="ff-card-emoji">' + o.e + '</span>' +
        '<div><div class="ff-card-label">' + o.l + '</div><div class="ff-card-sub">' + o.d + '</div></div>' +
      '</div>';
    }).join('');
    return dots(2) +
      '<h2 class="ff-title">What\'s her build? 🧍</h2>' +
      '<p class="ff-sub">Which best describes her body shape?</p>' +
      cards +
      '<div class="ff-btn-row">' +
        '<button class="ff-btn-secondary" data-action="go" data-step="2">← Back</button>' +
        '<button class="ff-btn-primary" data-action="go" data-step="4"' + (!state.build ? ' disabled' : '') + '>Next →</button>' +
      '</div>';
  }

  function renderFit() {
    var opts = [
      { v: 'perfect', l: 'Perfect fit right now', d: 'Fits her well today — great for sport, dance, or special occasions', e: '🎯' },
      { v: 'room',    l: 'Room to grow',           d: 'A little extra room — ideal for everyday wear and fast-growing girls', e: '🌱' },
      { v: 'relaxed', l: 'Relaxed & comfortable',  d: 'Loose and easy — perfect for lounging, play, and all-day wear', e: '😌' },
    ];
    var cards = opts.map(function (o) {
      return '<div class="ff-option-card' + (state.fit === o.v ? ' selected' : '') +
             '" data-action="pick" data-key="fit" data-val="' + o.v + '">' +
        '<span class="ff-card-emoji">' + o.e + '</span>' +
        '<div><div class="ff-card-label">' + o.l + '</div><div class="ff-card-sub">' + o.d + '</div></div>' +
      '</div>';
    }).join('');
    return dots(3) +
      '<h2 class="ff-title">How should it fit? 👗</h2>' +
      '<p class="ff-sub">What matters most to you and your daughter?</p>' +
      cards +
      '<div class="ff-btn-row">' +
        '<button class="ff-btn-secondary" data-action="go" data-step="3">← Back</button>' +
        '<button class="ff-btn-primary" data-action="go" data-step="5"' + (!state.fit ? ' disabled' : '') + '>See Her Size →</button>' +
      '</div>';
  }

  function renderResult() {
    var rec  = recommendSize(state.height, state.weight, state.build, state.fit);
    var next = getNextSize(rec);
    var cf   = getConfidence(state.height, rec, state.build, state.fit);

    var buildLabels = { slim: 'a slim build', regular: 'a regular build', broad: 'a broad / athletic build' };
    var fitLabels   = { perfect: 'a perfect fit right now', room: 'room to grow', relaxed: 'a relaxed comfortable fit' };

    // Show the chart data for the recommended size
    var chart = SIZE_CHART[rec];
    var measureNote = chart
      ? '<div class="ff-measure-note">' +
          '<div class="ff-measure-row"><span>Typical height</span><strong>' + chart.height + ' cm</strong></div>' +
          '<div class="ff-measure-row"><span>Chest</span><strong>' + chart.chest + ' cm</strong></div>' +
          '<div class="ff-measure-row"><span>Waist</span><strong>' + chart.waist + ' cm</strong></div>' +
          '<div class="ff-measure-row"><span>Hip</span><strong>' + chart.hip + ' cm</strong></div>' +
        '</div>'
      : '';

    var nextSizeBlock = next
      ? '<div class="ff-upsell">' +
          '<div class="ff-upsell-text">' +
            '<strong>🌱 Also available in Size ' + next + '</strong>' +
            '<span>She\'s growing fast — consider grabbing the next size up too.</span>' +
          '</div>' +
          '<button class="ff-upsell-btn" data-action="upsell" data-size="' + next + '">Select ' + next + '</button>' +
        '</div>'
      : '';

    return dots(4) +
      '<h2 class="ff-title">Her Perfect Fit! 🎉</h2>' +
      '<p class="ff-sub">Based on your answers and our size chart</p>' +
      '<div class="ff-result-hero">' +
        '<div class="ff-result-size">' + rec + '</div>' +
        '<div class="ff-result-label">Recommended Size</div>' +
        '<div class="ff-result-badge"><span class="check">✓</span> ' + cf + '% confidence — Great fit for her!</div>' +
      '</div>' +
      '<div class="ff-result-summary">' +
        'Based on her <strong>height of ' + state.height + ' cm</strong>, <strong>' + buildLabels[state.build] + '</strong>, and your preference for <strong>' + fitLabels[state.fit] + '</strong>, we recommend <strong>Size ' + rec + '</strong>.' +
      '</div>' +
      measureNote +
      nextSizeBlock +
      '<button class="ff-btn-primary" id="ff-apply-btn" data-action="apply" data-size="' + rec + '">Select Size ' + rec + ' →</button>' +
      '<div class="ff-restart"><button data-action="restart">Start over</button></div>';
  }

  /* ─────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────── */
  function render() {
    var content = document.getElementById('fit-finder-content');
    if (!content) return;
    var html = '';
    switch (state.step) {
      case 0: html = renderIntro();  break;
      case 1: html = renderAge();    break;
      case 2: html = renderHeight(); break;
      case 3: html = renderBuild();  break;
      case 4: html = renderFit();    break;
      case 5: html = renderResult(); break;
    }
    content.innerHTML = html;
    bindSliders();
  }

  function bindSliders() {
    var h = document.getElementById('ff-height');
    var w = document.getElementById('ff-weight');
    if (h) h.addEventListener('input', function () {
      state.height = parseInt(this.value, 10);
      var el = document.getElementById('ff-height-val');
      if (el) el.textContent = state.height + ' cm';
    });
    if (w) w.addEventListener('input', function () {
      state.weight = parseInt(this.value, 10);
      var el = document.getElementById('ff-weight-val');
      if (el) el.textContent = state.weight + ' kg';
    });
  }

  /* ─────────────────────────────────────────────
     EVENTS
  ───────────────────────────────────────────── */
  function handleClick(e) {
    var el = e.target.closest('[data-action]');
    if (!el) return;
    var action = el.dataset.action;

    if (action === 'go') {
      state.step = parseInt(el.dataset.step, 10);
      render();
    }
    if (action === 'pick') {
      state[el.dataset.key] = el.dataset.val;
      render();
    }
    if (action === 'apply') {
      var size = el.dataset.size;
      applySize(size);
      el.textContent = '✅ Size ' + size + ' selected — now add to cart!';
      el.classList.add('success');
      el.disabled = true;
      setTimeout(function () { closeModal(); }, 1400);
    }
    if (action === 'upsell') {
      applySize(el.dataset.size);
      el.textContent = '✅ Selected';
      el.disabled = true;
      setTimeout(function () { closeModal(); }, 1000);
    }
    if (action === 'restart') {
      state = { step: 0, age: null, height: 141, weight: 32, build: null, fit: null };
      render();
    }
  }

  /* ─────────────────────────────────────────────
     MODAL
  ───────────────────────────────────────────── */
  function openModal() {
    var overlay = document.getElementById('fit-finder-overlay');
    if (!overlay) return;
    overlay.setAttribute('aria-hidden', 'false');
    overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    render();
  }

  function closeModal() {
    var overlay = document.getElementById('fit-finder-overlay');
    if (!overlay) return;
    overlay.setAttribute('aria-hidden', 'true');
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  /* ─────────────────────────────────────────────
     INIT
  ───────────────────────────────────────────── */
  function init() {
    var trigger  = document.getElementById('fit-finder-trigger');
    var closeBtn = document.getElementById('fit-finder-close');
    var overlay  = document.getElementById('fit-finder-overlay');
    var content  = document.getElementById('fit-finder-content');

    if (!trigger || !overlay || !content) return;

    trigger.addEventListener('click', openModal);
    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) closeModal();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeModal();
    });
    content.addEventListener('click', handleClick);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();